"use client";

import {
  CTALinkProps,
  renderCTAMarkup,
} from "@/components/CTA/variants/CtaLink/shared";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import { ConversionTracked } from "@/hooks/eventing/types/core.conversions.ConversionTracked.v2";
import { getCorrelationID, hyphenateForTagular } from "@/utils/tagular/helpers";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { TagularAction, TagularCategory } from "@/types/Tagular";
import { useLogistrationModal } from "@/components/LogistrationModal/hooks/useLogistrationModal";

export default function ClientCTALink(props: CTALinkProps) {
  const { enable_tracking = true } = props;
  const webElement: ElementViewed["webElement"] = {
    elementType: props.elementType ?? props.element_type ?? ElementType.Button,
    htmlId: props.html_id,
    location: props.location as string,
    position: props.position ? props.position.toString() : "1",
    text: props.label,
    name: props.name
      ? hyphenateForTagular(props.name)
      : hyphenateForTagular(props.label),
  };

  const { handleLogistrationModalClick, shouldShowModal } =
    useLogistrationModal();

  const { elementViewed, elementClicked } = useElementEvents({ webElement });

  const { inView, ref } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView && enable_tracking) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  const onClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (shouldShowModal) {
      handleEventing();
      handleLogistrationModalClick(e, "signin", handleRedirect);
      return;
    }
    handleEventing();
  };

  const handleRedirect = () => {
    window.location.href = props.url;
  };

  const handleEventing = () => {
    if (enable_tracking) {
      elementClicked({ outboundUrl: props.url });
    }

    props.additionalClickEvent?.();

    if (props.conversionTrackingInfo) {
      const conversionEvent: ConversionTracked = {
        correlation: { id: getCorrelationID() },
        metadata: {
          action: props.action ?? TagularAction.STARTED,
          category: props.category ?? TagularCategory.ENROLLMENT,
          timestamp: new Date().toISOString(),
          customName: props.conversionTrackingInfo.customName,
          productList:
            props.conversionTrackingInfo?.productList?.map((product) => ({
              brand: hyphenateForTagular(product.brand),
              category: hyphenateForTagular(product.category),
              variant: translateVariant(product.variant),
              name: product.productName || "unknown",
              id: product.id,
              externalId: product.externalId,
            })) ?? [],
        },
      };
      const event = makeEvent<ConversionTracked, EVENTS>(conversionEvent);
      event<ConversionTracked>(EVENTS.ConversionTracked)();
    }
  };

  return (
    <>
      {renderCTAMarkup({
        ...props,
        onClick,
        ref,
      })}
    </>
  );
}
